/**
 * Saying on the 复习 settings page that a change has not been saved yet.
 *
 * The form saves when Save is pressed and not before, and a reader who flips a
 * switch and leaves has changed nothing. The bar at the foot of the window
 * holds the button, and this puts a line beside it once anything on the form
 * moves, so the press the page is waiting for is the one thing the bar says.
 * The line the last save wrote goes at the same moment, since it no longer
 * describes the form.
 *
 * **The page draws the line and this only shows it.** Its words are the
 * page's, in both languages and both writings, in
 * `src/user/fuxi-settings-copy.ts`. A page whose script has not run has a Save
 * button that works and no line, which is the page before this module.
 *
 * A save redraws the form, line hidden, and `./htmx-rebinding.ts` calls this
 * again over what came back. The swap replaces the form whole, so what came
 * back is the form itself, and the search starts from its parent to find it.
 * `boundOnce` keeps a form bound once however many times that happens.
 */

import { boundOnce } from "./bound-once.js";

/** The form, which says it is the settings form. */
const form = "form[data-cb-fuxi-settings]";

/** The line saying a change waits to be saved, drawn hidden. */
const unsaved = "[data-cb-fuxi-settings-unsaved]";

/** The line the last save wrote. */
const saved = "[data-cb-fuxi-settings-saved]";

/** The forms this bundle has not already bound. */
const notYetListening = boundOnce();

/** Show the line, and take away the one saying the form was saved. */
function markUnsaved(settings: HTMLElement): void {
  const line = settings.querySelector<HTMLElement>(unsaved);

  if (line === null || line.hidden !== true) {
    return;
  }

  line.hidden = false;

  const said = settings.querySelector(saved);

  if (said !== null) {
    said.textContent = "";
  }
}

/**
 * Watch the settings form for a change, where the page has one.
 *
 * @param within - The document, or what an htmx swap just put into it.
 */
export function startFuxiSettingsUnsaved(within: ParentNode): void {
  const from =
    within instanceof Element && within.parentNode !== null
      ? within.parentNode
      : within;

  for (const settings of notYetListening(from, form)) {
    const changed = (): void => {
      markUnsaved(settings);
    };

    settings.addEventListener("input", changed);
    settings.addEventListener("change", changed);
  }
}
